import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@sharedModule/material/material.module';
import { toDisplayableImageSrc } from '@sharedModule/models/gcs-image.model';
import { CommonService } from '@sharedModule/services/common/common.service';
import { Subject, takeUntil } from 'rxjs';
import { DocumentPreviewDialogData } from './document-preview-dialog.model';

/**
 * Shows an uploaded KYC document.
 *
 * The stored link points at a private GCS bucket and 403s when opened
 * directly, so the image is fetched through partner/getGcsImage and rendered
 * here rather than linked to. Showing it in a dialog also avoids the popup
 * blocker, which kills a window.open() issued after the response arrives.
 */
@Component({
  selector: 'app-document-preview-dialog',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './document-preview-dialog.component.html',
  styleUrl: './document-preview-dialog.component.scss',
})
export class DocumentPreviewDialogComponent implements OnInit, OnDestroy {
  imageSrc = '';
  isLoading = true;
  hasError = false;
  #destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocumentPreviewDialogData,
    private dialogRef: MatDialogRef<DocumentPreviewDialogComponent>,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.commonService
      .getGcsImage({
        gcs_image_link: this.data.gcsImageLink,
        partner_id: this.data.partnerId,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          this.imageSrc = x.success ? toDisplayableImageSrc(x.response) : '';
          this.hasError = !this.imageSrc;
          this.isLoading = false;
        },
        error: () => {
          this.hasError = true;
          this.isLoading = false;
        },
      });
  }

  /** The image decoded as something other than an image after all. */
  onImageError(): void {
    this.hasError = true;
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
